// Invite a group to set up a court-rental account.
//
// Lives on two pages: Court Rentals, beside the rate card the invite carries,
// and Content, where it was originally built. The same panel rather than two
// copies — the rate travelling with the invite is what keeps a group's own
// bookings priced the same as one an admin books for them, and two drifting
// copies of that form is exactly how that guarantee gets lost.
//
// Self-contained on purpose: it loads its own tiers and rates and owns its own
// alerts, so a page adds it with no wiring beyond rendering it.
import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { useBranding } from '../contexts/BrandingContext';
import {
  Box, Paper, Typography, Grid, TextField, MenuItem, Button, Alert, InputAdornment,
} from '@mui/material';

const EMPTY = {
  organizationName: '', contactFirstName: '', contactLastName: '',
  email: '', phone: '', tierId: '', rentalRateId: '', notes: '',
  deposit: '',   // dollars, blank = no deposit
};

export default function GroupRentalInvitePanel() {
  const branding = useBranding();
  // The tier grants access; the rate card prices their bookings. Both travel
  // with the invite, which is what provisionCourtRenter reads on the far side.
  const [rentalTiers, setRentalTiers] = useState([]);
  const [rentalRates, setRentalRates] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getDocs(collection(db, 'membershipTiers'))
      .then(snap => {
        const t = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(x => x.pricingMode === 'court_rental')
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        setRentalTiers(t);
        setForm(f => (f.tierId ? f : { ...f, tierId: t[0]?.id || '' }));
      })
      .catch(e => console.error('Failed to load rental tiers', e));
    getDocs(collection(db, 'rentalRates'))
      .then(snap => {
        const r = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.rateName || '').localeCompare(b.rateName || ''));
        setRentalRates(r);
        setForm(f => (f.rentalRateId ? f : { ...f, rentalRateId: r[0]?.id || '' }));
      })
      .catch(e => console.error('Failed to load rental rates', e));
  }, []);

  const send = async () => {
    const f = form;
    if (!f.organizationName.trim()) { setError('Organization name is required.'); return; }
    if (!f.email.trim()) { setError('Email is required.'); return; }
    if (!f.rentalRateId) { setError('Choose a rate — it is what prices their bookings.'); return; }
    if (f.deposit.trim() !== '' && !(parseFloat(f.deposit) >= 0)) {
      setError('Deposit must be a number, or blank for none.'); return;
    }
    setSending(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'sendGroupRentalInvite');
      await fn({
        email: f.email.trim(),
        organizationName: f.organizationName.trim(),
        contactFirstName: f.contactFirstName.trim(),
        contactLastName: f.contactLastName.trim(),
        phone: f.phone.trim(),
        tierId: f.tierId,
        rentalRateId: f.rentalRateId,
        // Dollars in the box, cents on the wire — money never travels as a float.
        depositCents: f.deposit.trim() === '' ? 0 : Math.round(parseFloat(f.deposit) * 100),
        notes: f.notes.trim(),
      });
      const tierName = rentalRates.find(r => r.id === f.rentalRateId)?.rateName || '';
      setSent(prev => [{ org: f.organizationName.trim(), email: f.email.trim(), tierName }, ...prev]);
      setSuccess(`Invite sent to ${f.email.trim()}. The link expires in 7 days.`);
      setTimeout(() => setSuccess(''), 6000);
      // Rate and tier are kept deliberately — several groups usually go onto the
      // same rate in one sitting. Everything identifying a person is cleared,
      // and so is the deposit: carrying a previous group's amount into the next
      // invite is exactly the mistake that gets noticed after it is sent.
      setForm({ ...EMPTY, tierId: f.tierId, rentalRateId: f.rentalRateId });
    } catch (e) {
      console.error('Group rental invite failed:', e);
      setError('Invite failed: ' + (e.message || String(e)));
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Box sx={{ maxWidth: 780 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: branding.chromeBg, mb: 0.5 }}>
          Invite a group to set up a rental account
        </Typography>
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
          They get a link to create their own login. Court Renter is not offered on the
          public signup form — this invite is the only way a rental account is created.
          The rate below is the Court Rentals rate card and travels with the
          invite, so their bookings cost the same as if you booked for them. They confirm
          their own liability insurance when they register.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <TextField fullWidth size="small" label="Organization *"
              placeholder="e.g. Orlando Rec League"
              value={form.organizationName} onChange={set('organizationName')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField fullWidth size="small" select label="Rate"
              value={form.rentalRateId} onChange={set('rentalRateId')}
              helperText={rentalRates.length ? 'From the Court Rentals rate card' : 'No rates configured'}>
              {rentalRates.map(r => {
                const bits = [];
                if (Number(r.hourlyRate) > 0) bits.push(`$${Number(r.hourlyRate)}`);
                if (Number(r.primeRate) > 0) bits.push(`$${Number(r.primeRate)} prime`);
                if (Number(r.weekendRate) > 0) bits.push(`$${Number(r.weekendRate)} wknd`);
                return (
                  <MenuItem key={r.id} value={r.id}>
                    {r.rateName}{bits.length ? ` — ${bits.join(' / ')}` : ''}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Contact first name"
              value={form.contactFirstName} onChange={set('contactFirstName')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Contact last name"
              value={form.contactLastName} onChange={set('contactLastName')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Phone"
              value={form.phone} onChange={set('phone')} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth size="small" label="Email *" type="email"
              value={form.email} onChange={set('email')} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth size="small" label="Note to include (optional)" multiline minRows={2}
              placeholder="e.g. Confirming your Tuesday 6-8pm block on courts 1-2"
              value={form.notes} onChange={set('notes')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth size="small" type="number" label="Deposit"
              value={form.deposit} onChange={set('deposit')}
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="Blank for none. Refundable, and they cannot book until it is paid." />
          </Grid>
          {rentalTiers.length > 1 && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" select label="Tier"
                value={form.tierId} onChange={set('tierId')}
                helperText="Grants court-booking rights; the rate above prices them">
                {rentalTiers.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Grid>
          )}
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="contained" onClick={send}
            disabled={sending || !form.organizationName.trim() || !form.email.trim() || !form.rentalRateId}
            sx={{ backgroundColor: branding.chromeBg }}>
            {sending ? 'Sending…' : 'Send Invite'}
          </Button>
          <Typography variant="caption" sx={{ color: '#888' }}>Link expires in 7 days.</Typography>
        </Box>
      </Paper>

      {sent.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Sent this session</Typography>
          {sent.map((s, i) => (
            <Typography key={i} variant="body2" sx={{ color: '#555' }}>
              {s.org} — {s.email}{s.tierName ? ` · ${s.tierName}` : ''}
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
}
